export const createChatMessage = (text, sender, extra = {}) => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  text,
  sender,
  timestamp: new Date().toISOString(),
  ...extra,
});

const unescapeJsonString = (value) =>
  String(value)
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\r')
    .replace(/\\t/g, '\t')
    .replace(/\\"/g, '"')
    .replace(/\\\\/g, '\\');

/** Pull plain reply text out of Gemini JSON (including broken/truncated JSON). */
export const extractReplyFromJsonish = (text) => {
  if (text == null || text === '') return '';

  let cleaned = String(text).trim();
  cleaned = cleaned.replace(/^```(?:json)?\s*/i, '').replace(/\s*```\s*$/i, '').trim();

  const looksLikeJson =
    cleaned.startsWith('{') || cleaned.startsWith('"reply"') || /"reply"\s*:/.test(cleaned);

  if (!looksLikeJson) {
    return unescapeJsonString(cleaned).trim();
  }

  try {
    const parsed = JSON.parse(cleaned);
    if (typeof parsed?.reply === 'string') {
      return unescapeJsonString(parsed.reply).trim();
    }
  } catch {
    /* partial / multiline JSON */
  }

  const closedPatterns = [
    /"reply"\s*:\s*"([\s\S]*?)"\s*,\s*"actions"/i,
    /"reply"\s*:\s*"([\s\S]*?)"\s*}\s*$/i,
    /"reply"\s*:\s*"([\s\S]*?)"\s*,\s*\{/i,
  ];
  for (const pattern of closedPatterns) {
    const match = cleaned.match(pattern);
    if (match?.[1]) {
      return unescapeJsonString(match[1]).trim();
    }
  }

  const truncated = cleaned.match(/"reply"\s*:\s*"([\s\S]+)/i);
  if (truncated?.[1]) {
    let inner = truncated[1];
    inner = inner.replace(/"\s*,\s*"actions"[\s\S]*$/i, '');
    inner = inner.replace(/"\s*}\s*$/i, '');
    return unescapeJsonString(inner).trim();
  }

  cleaned = cleaned.replace(/^\{\s*/i, '');
  cleaned = cleaned.replace(/^"reply"\s*:\s*"/i, '');
  cleaned = cleaned.replace(/"\s*,\s*"actions"[\s\S]*$/i, '');
  cleaned = cleaned.replace(/"\s*}\s*$/i, '');

  return unescapeJsonString(cleaned).trim();
};

/** Turn literal \n and accidental JSON wrappers into readable chat text. */
export const normalizeChatReply = (text) => extractReplyFromJsonish(text);

/** Remove internal app routes from user-facing chat text. */
export const stripInternalRoutePaths = (text) => {
  if (!text) return '';

  let cleaned = String(text);

  cleaned = cleaned.replace(/`(\/[a-z0-9][a-z0-9\-/_]*\/?)`/gi, '');
  cleaned = cleaned.replace(
    /(?<![\w])\/(?:vendor|client|admin|bookings?|browse-vendors|dashboard|favorites|messages|help-center|billing|register|login|booking)(?:\/[a-z0-9\-_{}]+)*/gi,
    ''
  );

  cleaned = cleaned
    .replace(/,?\s*or go directly to\s*[.,]?/gi, '')
    .replace(/,?\s*or open path:\s*[.,]?/gi, '')
    .replace(/,?\s*(?:or )?use the direct path:\s*[.,]?/gi, '')
    .replace(/,?\s*you can always find the full list at\s*[.,]?/gi, '')
    .replace(/\(\s*\)/g, '')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\s+([,.;:])/g, '$1')
    .replace(/^\s*[,;:]\s*/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return cleaned;
};

export const formatChatDisplayText = (text) =>
  stripInternalRoutePaths(normalizeChatReply(text));

export const formatChatTime = (date) => {
  try {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

export const renderChatMarkdownLite = (text) => {
  const normalized = formatChatDisplayText(text);
  const parts = normalized.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="font-semibold">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <span key={i}>{part}</span>;
  });
};

export const buildChatHistory = (msgs) =>
  msgs
    .filter((m) => m.sender === 'user' || m.sender === 'bot')
    .filter((m) => !m.isWelcome)
    .slice(-20)
    .map((m) => ({
      role: m.sender === 'user' ? 'user' : 'assistant',
      text: m.text,
    }));

export const loadStoredChat = (storageKey, fallbackMessage) => {
  try {
    const raw = sessionStorage.getItem(storageKey);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed.map((m) => ({
          ...m,
          timestamp: m.timestamp || new Date().toISOString(),
        }));
      }
    }
  } catch {
    /* ignore */
  }
  return [createChatMessage(fallbackMessage, 'bot', { isWelcome: true })];
};

export const persistChat = (storageKey, messages) => {
  if (!storageKey || messages.length === 0) return;
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(messages.slice(-40)));
  } catch {
    /* ignore */
  }
};

const NOTIFICATION_SOUND_URL = `${(process.env.PUBLIC_URL || '').replace(/\/$/, '')}/sound/notificationSound.mp3`;

let notificationAudio = null;
let audioUnlocked = false;
let soundPending = false;
let listenersAttached = false;

const getNotificationAudio = () => {
  if (!notificationAudio) {
    notificationAudio = new Audio(NOTIFICATION_SOUND_URL);
    notificationAudio.preload = 'auto';
    notificationAudio.volume = 0.5;
  }
  return notificationAudio;
};

const unlockNotificationAudio = async () => {
  if (audioUnlocked) return true;
  const audio = getNotificationAudio();
  const volume = audio.volume;
  try {
    audio.volume = 0.001;
    await audio.play();
    audio.pause();
    audio.currentTime = 0;
    audio.volume = volume;
    audioUnlocked = true;
    return true;
  } catch {
    return false;
  }
};

const playNotificationNow = async () => {
  const audio = getNotificationAudio();
  audio.currentTime = 0;
  audio.volume = 0.5;
  await audio.play();
  audioUnlocked = true;
  soundPending = false;
};

/** Call once on app load so the first click/tap can unlock audio (browser autoplay policy). */
export const setupChatNotificationSoundListeners = () => {
  if (listenersAttached || typeof document === 'undefined') {
    return () => {};
  }
  listenersAttached = true;

  getNotificationAudio().load();

  const onUserGesture = async () => {
    await unlockNotificationAudio();
    if (!soundPending) return;
    try {
      await playNotificationNow();
    } catch {
      /* still blocked — keep pending */
    }
  };

  const opts = { capture: true, passive: true };
  ['pointerdown', 'touchstart', 'keydown'].forEach((event) => {
    document.addEventListener(event, onUserGesture, opts);
  });

  return () => {
    ['pointerdown', 'touchstart', 'keydown'].forEach((event) => {
      document.removeEventListener(event, onUserGesture, opts);
    });
    listenersAttached = false;
  };
};

/**
 * Play concierge notification sound.
 * @param {{ pendingIfBlocked?: boolean }} options — queue for next user click if autoplay is blocked
 */
export const playChatNotificationSound = async ({ pendingIfBlocked = false } = {}) => {
  try {
    await playNotificationNow();
  } catch {
    if (pendingIfBlocked) {
      soundPending = true;
    }
  }
};
